import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, Send, Info } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { submitEnquiry, errorMessage } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const inputCls = "h-12 rounded-xl border-plum/15 bg-white text-[15px] focus-visible:ring-2 focus-visible:ring-teal/40 focus-visible:ring-offset-0";

const Field = ({ id, label, required, optional, error, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm font-semibold text-ink">
      {label} {required && <span className="text-magenta" aria-hidden="true">*</span>}
      {optional && <span className="font-normal text-slate">(optional)</span>}
    </Label>
    {children}
    {error && <p id={`${id}-error`} role="alert" className="text-xs font-medium text-destructive" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

const SelectField = ({ id, value, onChange, options, placeholder, invalid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger id={id} data-testid={id} aria-invalid={invalid} className={`${inputCls} ${invalid ? "border-destructive" : ""}`}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o.value ?? o} value={o.value ?? o} data-testid={`${id}-option-${(o.value ?? o).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          {o.label ?? o}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/**
 * Reusable enquiry form.
 * mode: "partnership" | "investment" | "contact" (contact lets the visitor choose the enquiry type)
 */
export const EnquiryForm = ({ mode = "contact", interests = [], interestLabel = "Partnership interest", defaultType = "general", idPrefix = "enquiry" }) => {
  const init = { type: mode === "contact" ? defaultType : mode, name: "", organisation: "", role: "", email: "", phone: "", location: "", interest: "", message: "", consent: false, website: "" };
  const [v, setV] = useState(init);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [serverError, setServerError] = useState("");
  const [result, setResult] = useState(null);
  const id = (k) => `${idPrefix}-${k}`;
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const needsOrg = mode !== "contact" || v.type !== "general";

  const validate = () => {
    const e = {};
    if (v.name.trim().length < 2) e.name = "Please enter your full name.";
    if (!EMAIL_RE.test(v.email.trim())) e.email = "Please enter a valid email address.";
    if (v.phone && !/^[0-9+\-() ]{7,20}$/.test(v.phone.trim())) e.phone = "Use digits, spaces and + - ( ) only.";
    if (needsOrg && !v.organisation.trim()) e.organisation = "Please enter your organisation (or 'Independent').";
    if (mode !== "contact" && !v.role.trim()) e.role = "Please enter your professional role.";
    if (mode !== "contact" && !v.location.trim()) e.location = "Please enter your city or district.";
    if (interests.length && mode !== "contact" && !v.interest) e.interest = "Please choose an option.";
    if (v.message.trim().length < 10) e.message = "Please write at least a short message (10+ characters).";
    if (!v.consent) e.consent = "Please confirm you have read the privacy notice.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      document.getElementById(id(Object.keys(e)[0]))?.focus();
      return;
    }
    setStatus("sending");
    setServerError("");
    try {
      const clean = Object.fromEntries(Object.entries(v).map(([k, val]) => [k, typeof val === "string" ? val.trim() || null : val]));
      const data = await submitEnquiry(clean);
      setResult(data);
      setStatus("success");
      toast.success("Enquiry received — thank you.");
    } catch (err) {
      setServerError(errorMessage(err));
      setStatus("error");
      toast.error("Your enquiry could not be sent.");
    }
  };

  if (status === "success") {
    return (
      <div data-testid={`${idPrefix}-success`} role="status" className="rounded-2xl border border-teal/25 bg-teal-50 p-8 sm:p-10">
        <CheckCircle2 className="h-10 w-10 text-teal-700" strokeWidth={1.6} />
        <h3 className="mt-5 text-2xl font-semibold">Thank you, {v.name.split(" ")[0]}.</h3>
        <p className="mt-3 leading-relaxed text-slate">
          Your enquiry has been received and securely recorded.
          {result?.email_delivered ? " The SwastSaathi team has been notified by email and will respond to you at " : " Our email notification is delayed, but your message is saved and the team will respond to you at "}
          <strong className="text-ink">{v.email}</strong>.
        </p>
        <p className="mt-4 text-sm text-slate">Please note: submitting this form is an enquiry only and does not establish a formal partnership, investment or service relationship.</p>
        <button type="button" onClick={() => { setV(init); setStatus("idle"); setErrors({}); }} data-testid={`${idPrefix}-send-another`} className="mt-6 text-sm font-semibold text-teal-700 underline-offset-4 hover:underline">
          Send another enquiry
        </button>
      </div>
    );
  }

  const err = (k) => ({ "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `${id(k)}-error` : undefined });

  return (
    <form noValidate onSubmit={onSubmit} data-testid={`${idPrefix}-form`} className="space-y-6">
      <div className="grid gap-6 sm:grid-cols-2">
        <Field id={id("name")} label="Name" required error={errors.name}>
          <Input id={id("name")} data-testid={id("name")} autoComplete="name" value={v.name} onChange={set("name")} className={inputCls} {...err("name")} />
        </Field>
        <Field id={id("email")} label="Email" required error={errors.email}>
          <Input id={id("email")} data-testid={id("email")} type="email" autoComplete="email" value={v.email} onChange={set("email")} className={inputCls} {...err("email")} />
        </Field>
        <Field id={id("organisation")} label="Organisation" required={needsOrg} optional={!needsOrg} error={errors.organisation}>
          <Input id={id("organisation")} data-testid={id("organisation")} autoComplete="organization" value={v.organisation} onChange={set("organisation")} className={inputCls} {...err("organisation")} />
        </Field>
        <Field id={id("role")} label="Professional role" required={mode !== "contact"} optional={mode === "contact"} error={errors.role}>
          <Input id={id("role")} data-testid={id("role")} autoComplete="organization-title" value={v.role} onChange={set("role")} className={inputCls} {...err("role")} />
        </Field>
        <Field id={id("phone")} label="Phone" optional error={errors.phone}>
          <Input id={id("phone")} data-testid={id("phone")} type="tel" autoComplete="tel" value={v.phone} onChange={set("phone")} className={inputCls} {...err("phone")} />
        </Field>
        <Field id={id("location")} label="Location" required={mode !== "contact"} optional={mode === "contact"} error={errors.location}>
          <Input id={id("location")} data-testid={id("location")} placeholder="City / district, state" value={v.location} onChange={set("location")} className={inputCls} {...err("location")} />
        </Field>
      </div>
      {interests.length > 0 && (
        <Field id={id("interest")} label={interestLabel} required={mode !== "contact"} error={errors.interest}>
          <SelectField id={id("interest")} value={v.interest} onChange={set("interest")} options={interests} placeholder="Select an option" invalid={!!errors.interest} />
        </Field>
      )}
      <Field id={id("message")} label="Message" required error={errors.message}>
        <Textarea id={id("message")} data-testid={id("message")} rows={5} value={v.message} onChange={set("message")} className="rounded-xl border-plum/15 text-[15px] focus-visible:ring-2 focus-visible:ring-teal/40 focus-visible:ring-offset-0" {...err("message")} />
      </Field>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={v.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      <div className="rounded-xl bg-mint/70 p-4 text-sm leading-relaxed text-slate">
        <p className="flex gap-2"><Info className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" />
          <span>Please do not share personal medical information. Your details are used only to respond to this enquiry. This form is an enquiry and does not establish a formal partnership or agreement.</span>
        </p>
      </div>
      <div className="space-y-2">
        <div className="flex items-start gap-3">
          <Checkbox id={id("consent")} data-testid={id("consent")} checked={v.consent} onCheckedChange={(c) => setV((s) => ({ ...s, consent: c === true }))} className="mt-0.5 h-5 w-5 rounded-md border-plum/30 data-[state=checked]:border-teal data-[state=checked]:bg-teal" {...err("consent")} />
          <Label htmlFor={id("consent")} className="text-sm font-normal leading-relaxed text-slate">
            I agree that SwastSaathi may use these details to respond to my enquiry, as described in the{" "}
            <Link to="/privacy" className="font-semibold text-teal-700 underline-offset-4 hover:underline" data-testid={id("privacy-link")}>privacy notice</Link>.
          </Label>
        </div>
        {errors.consent && <p id={`${id("consent")}-error`} role="alert" className="text-xs font-medium text-destructive" data-testid={`${id("consent")}-error`}>{errors.consent}</p>}
      </div>
      {status === "error" && (
        <div role="alert" data-testid={`${idPrefix}-error`} className="flex gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          <AlertCircle className="h-5 w-5 shrink-0" /> <span>{serverError}</span>
        </div>
      )}
      <button
        type="submit"
        disabled={status === "sending"}
        data-testid={`${idPrefix}-submit`}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal px-7 py-4 font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-teal-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
      >
        {status === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Send enquiry <Send className="h-4 w-4" /></>}
      </button>
    </form>
  );
};
